import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useLocalStorage } from '../hooks/useLocalStorage'; // Import useLocalStorage
import { useBodyScrollLock } from '../hooks/useBodyScrollLock';
import {
    ModalOverlay,
    ModalContent as StyledModalContent, // Renamed to avoid conflict
    ModalInnerContent,
    HeaderLeft,
    ModalHeader,
    TodoList,
    TodoItem,
    NewTodoItem,
    Checkbox,
    TodoInput,
    CloseButton,
    DeleteButton
} from '../styles/ProjectChecklistModal.styles';
import { VisibilityToggleIcon } from '../components/icons/VisibilityToggleIcon';
import { SettingsIcon } from '../components/icons/SettingsIcon'; // Import settings icon
import BackgroundPicker from './BackgroundPicker'; // Import BackgroundPicker
import { doc, updateDoc, onSnapshot, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { initializeUserProjectSettings } from '../utils/firebaseUtils'; // For default settings

// Styled component for ModalContent that will have the background
const ModalContent = styled(StyledModalContent)`
    background: ${props => {
        if (!props.background) return 'rgba(0, 0, 0, 1)';
        if (props.background.type === 'color') return props.background.color;
        return `url(${props.background.urls?.full || ''})`;
    }};
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    transition: background 0.3s ease-in-out;
    color: ${props => props.background?.fontColor || '#fff'} !important;

    * {
        color: ${props => props.background?.fontColor || '#fff'} !important;
    }

    input, textarea, button {
        color: inherit !important;
    }

    svg {
        color: ${props => props.background?.fontColor || '#fff'} !important;
        stroke: ${props => props.background?.fontColor || '#fff'} !important;
    }
`;


const ThinkingItemsModal = ({ onClose }) => {
    useBodyScrollLock();
    const { user } = useAuth();
    const navigate = useNavigate();
    // Use useLocalStorage for items, ensuring user-specific key
    const [items, setItems] = useLocalStorage(
        user ? `thinking-items-${user.uid}` : 'thinking-items-guest', // Guest key if user not yet loaded
        []
    );
    const [newItemText, setNewItemText] = useState('');
    const [isInputFocused, setIsInputFocused] = useState(false);
    const [showCompleted, setShowCompleted] = useState(() => {
        const saved = localStorage.getItem(`thinking-items-show-completed`);
        return saved !== null ? JSON.parse(saved) : false;
    });
    const [ideasPlanBackground, setIdeasPlanBackground] = useState(null);
    const [showBackgroundPicker, setShowBackgroundPicker] = useState(false);
    const [newlyAddedItemId, setNewlyAddedItemId] = useState(null);
    const inputRefs = useRef({});

    useEffect(() => {
        if (newlyAddedItemId && inputRefs.current[newlyAddedItemId]) {
            const inputElement = inputRefs.current[newlyAddedItemId];
            inputElement.focus();
            inputElement.selectionStart = inputElement.selectionEnd = inputElement.value.length;
            setNewlyAddedItemId(null);
        }
    }, [newlyAddedItemId]);


    useEffect(() => {
        localStorage.setItem(`thinking-items-show-completed`, JSON.stringify(showCompleted));
    }, [showCompleted]);

    useEffect(() => {
        if (!user) return;

        // Key for localStorage needs to be updated if user.uid becomes available after initial render
        // This effect handles migrating from a guest key to a user-specific key if needed.
        const guestKey = 'thinking-items-guest';
        const userKey = `thinking-items-${user.uid}`;
        const guestData = localStorage.getItem(guestKey);

        if (guestData && userKey !== guestKey) {
            const parsedGuestData = JSON.parse(guestData);
            // Set items with user-specific key, potentially merging if user already had data
            // For simplicity, this example prioritizes guest data if it exists during transition.
            // A more complex merge might be needed depending on requirements.
            const userSpecificData = localStorage.getItem(userKey);
            if (!userSpecificData && parsedGuestData.length > 0) { // Only migrate if no user-specific data yet and guest data exists
                setItems(parsedGuestData); // This will write to userKey due to useLocalStorage's effect
            }
            localStorage.removeItem(guestKey); // Clean up guest data
        }


        // Fetch and set user settings including ideasPlanBackground
        const userSettingsRef = doc(db, 'userSettings', user.uid);
        const unsubscribeUserSettings = onSnapshot(userSettingsRef, async (docSnap) => {
            if (docSnap.exists()) {
                const settings = docSnap.data();
                setIdeasPlanBackground(settings.ideasPlanBackground);
            } else {
                await initializeUserProjectSettings(user.uid);
            }
        }, (error) => {
            console.error('Error fetching user settings for Ideas Plan:', error);
        });
        
        // Firebase listener for todos
        const userTodosRef = doc(db, 'userTodos', user.uid);
        const unsubscribeTodos = onSnapshot(userTodosRef, (docSnap) => {
            if (docSnap.exists()) {
                const firebaseItems = docSnap.data().items || [];
                setItems(firebaseItems); // This updates both state and localStorage via useLocalStorage
            } else {
                // Document doesn't exist, ensure local state and localStorage are also empty
                setItems([]);
            }
        }, (error) => {
            console.error('Error fetching user todos from Firebase:', error);
        });

        return () => {
            unsubscribeUserSettings();
            unsubscribeTodos();
        };
    }, [user, setItems]); // Add setItems to dependency array as it's from useLocalStorage

    const handleSelectBackground = async (background) => {
        if (!user) return;
        setIdeasPlanBackground(background);
        const userSettingsRef = doc(db, 'userSettings', user.uid);
        try {
            await updateDoc(userSettingsRef, {
                ideasPlanBackground: background,
                updatedAt: new Date().toISOString()
            });
        } catch (error) {
            // If the document doesn't exist, create it.
            if (error.code === 'not-found') {
                await setDoc(userSettingsRef, {
                    ideasPlanBackground: background,
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString()
                });
            } else {
                console.error('Error updating ideas plan background:', error);
            }
        }
        setShowBackgroundPicker(false); // Close picker after selection
    };

    const handleAddItem = async (options = {}) => {
        const { text = '', index = -1, fromEvent = null } = options;
        fromEvent?.preventDefault();

        const content = index === -1 ? (text || newItemText.trim()) : text;

        if (index === -1 && !content) return;

        const newItem = {
            id: crypto.randomUUID(),
            text: content,
            completed: false,
            createdAt: new Date().toISOString()
        };

        const updatedItems = [...items];
        if (index > -1) {
            updatedItems.splice(index, 0, newItem);
        } else {
            updatedItems.push(newItem);
        }

        setItems(updatedItems);
        setNewlyAddedItemId(newItem.id);

        if (index === -1) {
            setNewItemText('');
        }

        try {
            await updateDoc(doc(db, 'userTodos', user.uid), {
                items: updatedItems,
                updatedAt: new Date().toISOString()
            });
        } catch (error) {
            console.error('Error adding/updating user todo:', error);
        }
    };

    const handleToggleItem = async (itemId) => {
        const updatedItems = items.map(item => {
            if (item.id === itemId) {
                return { ...item, completed: !item.completed };
            }
            return item;
        });

        setItems(updatedItems);

        try {
            await updateDoc(doc(db, 'userTodos', user.uid), {
                items: updatedItems,
                updatedAt: new Date().toISOString()
            });
        } catch (error) {
            console.error('Error toggling user todo:', error);
        }
    };

    const handleDeleteItem = async (itemId) => {
        const updatedItems = items.filter(item => item.id !== itemId);
        setItems(updatedItems);

        try {
            await updateDoc(doc(db, 'userTodos', user.uid), {
                items: updatedItems,
                updatedAt: new Date().toISOString()
            });
        } catch (error) {
            console.error('Error deleting user todo:', error);
        }
    };

    const handleUpdateItem = async (itemId, newText) => {
        if (!newText.trim()) {
            handleDeleteItem(itemId);
            return;
        }

        const updatedItems = items.map(item => {
            if (item.id === itemId) {
                return { ...item, text: newText };
            }
            return item;
        });

        setItems(updatedItems);

        try {
            await updateDoc(doc(db, 'userTodos', user.uid), {
                items: updatedItems,
                updatedAt: new Date().toISOString()
            });
        } catch (error) {
            console.error('Error updating user todo:', error);
        }
    };

    const handleFocusItem = async (itemId) => {
        const updatedItems = items.map(item => {
            if (item.id === itemId) {
                return { ...item, focused: !item.focused };
            }
            return item;
        });

        setItems(updatedItems);

        try {
            await updateDoc(doc(db, 'userTodos', user.uid), {
                items: updatedItems,
                updatedAt: new Date().toISOString()
            });
        } catch (error) {
            console.error('Error handling focus item:', error);
        }
    };

    const onDragEnd = async (result) => {
        const { destination, source } = result;

        if (!destination) return;

        if (
            destination.droppableId === source.droppableId &&
            destination.index === source.index
        ) {
            return;
        }

        const visibleItems = items
            .map((item, index) => ({ item, index }))
            .filter(item => showCompleted || !item.item.completed);

        const sourceIndex = visibleItems[source.index].index;
        const destinationIndex = visibleItems[destination.index]?.index ?? items.length;

        const newItems = Array.from(items);
        const [removed] = newItems.splice(sourceIndex, 1);
        newItems.splice(destinationIndex, 0, removed);

        setItems(newItems);

        try {
            await updateDoc(doc(db, 'userTodos', user.uid), {
                items: newItems,
                updatedAt: new Date().toISOString()
            });
        } catch (error) {
            console.error('Error saving reordered user todos:', error);
        }
    };

    const handleKeyDown = (e, index) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            if (index !== undefined) {
                const visibleItem = visibleItems[index];
                const realIndex = items.findIndex(item => item.id === visibleItem.id);
                handleAddItem({ index: realIndex + 1 });
            } else {
                handleAddItem({ fromEvent: e });
            }
        }
    };

    const sortedItems = [...items].sort((a, b) => {
        if (a.focused && !b.focused) return -1;
        if (!a.focused && b.focused) return 1;
        return 0;
    });

    const visibleItems = sortedItems.filter(item => showCompleted || !item.completed);

    const completedCount = items.filter(item => item.completed).length;
    const totalCount = items.length;

    const [quickAddText, setQuickAddText] = useState('');

    const handleQuickAdd = async (e) => {
        e?.preventDefault();
        if (!quickAddText.trim()) return;

        const newItem = {
            id: `todo-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            text: quickAddText.trim(),
            completed: false,
            createdAt: new Date().toISOString()
        };

        const updatedItems = [...items, newItem];
        setItems(updatedItems);
        setQuickAddText('');

        try {
            await updateDoc(doc(db, 'userTodos', user.uid), {
                items: updatedItems,
                updatedAt: new Date().toISOString()
            });
        } catch (error) {
            console.error('Error adding user todo:', error);
        }
    };

    const handleQuickAddKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleQuickAdd();
        }
    };

    const handleCloseModal = () => {
        navigate('/'); // Navigate to dashboard home, which will trigger modal close in Dashboard.js
        if (onClose) { // Call original onClose if it exists (e.g. for other cleanup)
            onClose();
        }
    };

    return (
        <ModalOverlay onClick={handleCloseModal}>
            <ModalContent
                onClick={e => e.stopPropagation()}
                background={ideasPlanBackground} // Use state variable for background
            >
                {showBackgroundPicker && (
                    <BackgroundPicker
                        currentBackground={ideasPlanBackground}
                        onSelectBackground={handleSelectBackground}
                        projectTitle="Ideas Plan"
                        onClose={() => setShowBackgroundPicker(false)}
                    />
                )}
                {!showBackgroundPicker && (
                    <ModalInnerContent>
                        <ModalHeader>
                            <h2 style={{marginRight: '8px'}}>Ideas</h2>
                            <div>
                                <CloseButton
                                    as="button"
                                    onClick={() => setShowBackgroundPicker(true)}
                                    title="Change Background"
                                    style={{padding: 0, fontSize: '18px', lineHeight: '1', display: 'flex', alignItems: 'center', marginRight: '10px'}}
                                >
                                    <SettingsIcon />
                                </CloseButton>
                                <span style={{ color: '#fff', marginRight: '16px', display: 'flex', alignItems: 'center' }}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '4px' }}>
                                        <polyline points="20 6 9 17 4 12" />
                                    </svg>
                                    {completedCount}/{totalCount}
                                </span>
                                <CloseButton
                                    as="button"
                                    onClick={() => setShowCompleted(!showCompleted)}
                                    title={showCompleted ? "Hide checked items" : "Show checked items"}
                                    style={{padding: 0, fontSize: '18px', lineHeight: '1', display: 'flex', alignItems: 'center'}}
                                >
                                    <VisibilityToggleIcon hidden={!showCompleted} />
                                </CloseButton>
                                <CloseButton onClick={handleCloseModal}>×</CloseButton>
                            </div>
                        </ModalHeader>
                        <div style={{ marginBottom: '8px', padding: '0 16px' }}>
                        <TodoInput
                            value={quickAddText}
                            onChange={(e) => setQuickAddText(e.target.value)}
                            onKeyDown={handleQuickAddKeyDown}
                            onBlur={() => {
                                if (quickAddText.trim()) {
                                    handleQuickAdd();
                                }
                            }}
                            placeholder="What's on your mind?"
                            style={{ width: '100%', color: '#fff', borderBottom: '1px solid #444' }}
                        />
                    </div>

                    <DragDropContext onDragEnd={onDragEnd}>
                        <Droppable droppableId="thinking-items">
                            {(provided) => (
                                <TodoList
                                    ref={provided.innerRef}
                                    {...provided.droppableProps}
                                >
                                    {visibleItems.map((item, index) => (
                                        <Draggable
                                            key={item.id}
                                            draggableId={item.id}
                                            index={index}
                                        >
                                            {(provided, snapshot) => (
                                                <TodoItem
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    isDragging={snapshot.isDragging}
                                                >
                                                    <div {...provided.dragHandleProps}>
                                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                            <path d="M3 3C4.1 3 5 3.9 5 5C5 6.1 4.1 7 3 7C1.9 7 1 6.1 1 5C1 3.9 1.9 3 3 3ZM10 3C11.1 3 12 3.9 12 5C12 6.1 11.1 7 10 7C8.9 7 8 6.1 8 5C8 3.9 8.9 3 10 3ZM3 10C4.1 10 5 10.9 5 12C5 13.1 4.1 14 3 14C1.9 14 1 13.1 1 12C1 10.9 1.9 10 3 10ZM10 10C11.1 10 12 10.9 12 12C12 13.1 11.1 14 10 14C8.9 14 8 13.1 8 12C8 10.9 8.9 10 10 10ZM3 17C4.1 17 5 17.9 5 19C5 20.1 4.1 21 3 21C1.9 21 1 20.1 1 19C1 17.9 1.9 17 3 17ZM10 17C11.1 17 12 17.9 12 19C12 20.1 11.1 21 10 21C8.9 21 8 20.1 8 19C8 17.9 8.9 17 10 17Z" fill="#fff"/>
                                                        </svg>
                                                    </div>
                                                    <Checkbox
                                                        type="checkbox"
                                                        checked={item.completed}
                                                        onChange={() => handleToggleItem(item.id)}
                                                    />
                                                    <TodoInput
                                                        as="textarea"
                                                        value={item.text}
                                                        onChange={(e) => {
                                                            e.target.style.height = '24px';
                                                            e.target.style.height = `${e.target.scrollHeight}px`;
                                                            handleUpdateItem(item.id, e.target.value)
                                                        }}
                                                        onKeyDown={(e) => handleKeyDown(e, index)}
                                                        completed={item.completed}
                                                        style={{ overflowY: 'hidden' }}
                                                        ref={(el) => {
                                                            inputRefs.current[item.id] = el;
                                                            if (el) {
                                                                el.style.height = '24px';
                                                                el.style.height = `${el.scrollHeight}px`;
                                                            }
                                                        }}
                                                    />
                                                    <DeleteButton
                                                        onClick={() => handleFocusItem(item.id)}
                                                        style={{ marginRight: '8px', opacity: item.focused ? 1 : undefined }}
                                                        title={item.focused ? "Remove from focused" : "Add to focused"}
                                                    >
                                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                            <circle cx="12" cy="12" r="10" />
                                                            <circle cx="12" cy="12" r="3" fill={item.focused ? "currentColor" : "none"}/>
                                                        </svg>
                                                    </DeleteButton>
                                                    <DeleteButton
                                                        onClick={() => handleDeleteItem(item.id)}
                                                    >
                                                        ×
                                                    </DeleteButton>
                                                </TodoItem>
                                            )}
                                        </Draggable>
                                    ))}
                                    {provided.placeholder}
                                    <NewTodoItem isFocused={isInputFocused}>
                                        <Checkbox type="checkbox" disabled />
                                        <TodoInput
                                            value={newItemText}
                                            onChange={(e) => setNewItemText(e.target.value)}
                                            onKeyDown={handleKeyDown}
                                            onFocus={() => setIsInputFocused(true)}
                                            onBlur={() => {
                                                setIsInputFocused(false);
                                                if (newItemText.trim()) {
                                                    handleAddItem({text: newItemText});
                                                }
                                            }}
                                            placeholder="What's on your mind?"
                                        />
                                    </NewTodoItem>
                                </TodoList>
                            )}
                        </Droppable>
                    </DragDropContext>
                </ModalInnerContent>
                )} {/* End of !showBackgroundPicker conditional rendering */}
            </ModalContent>
        </ModalOverlay>
    );
};

export default ThinkingItemsModal;
